import { Component, computed, Input, OnInit, inject } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { SystemReport } from '../../../../models/api/reports/all-reports-response';
import { ReportTemplate } from '../../../../models/api/reports/report';
import { ReportCategoryUtil } from '../../../../models/reports/report-category';
import { CasePipe } from '../../../../pipes/case.pipe';
import { ReportPreviewMappingService } from '../../../../services/reports/report-preview-mapping.service';
import { ReportsService } from '../../../../services/reports/reports.service';
import { BaseComponent } from '../../../base/base.component';
import { ButtonComponent } from '../../../core/buttons/button.component';
import { FilterInputComponent } from '../../../core/controls/filter-input.component';
import { GalleriaComponent } from '../../../core/galleria/galleria.component';
import { PageHeaderComponent } from '../../../core/labels/page-header.component';
import { RequiredFieldsNoteComponent } from '../../../core/labels/required-fields-note.component';
import { ToastComponent } from '../../../core/labels/toast.component';
import { ActionRowComponent } from '../../../core/panels/action-row.component';
import { LoadingPanelComponent } from '../../../core/panels/loading-panel.component';
import { StepperComponent } from '../../../core/stepper/stepper.component';
import { ReportComponent } from '../report.component';
import { HandleLoadingState } from '../../../../decorators/handle-loading-state.decorator';

@Component({
  selector: 'app-select-report-step',
  imports: [
    LoadingPanelComponent,
    PageHeaderComponent,
    ActionRowComponent,
    ButtonComponent,
    ReportComponent,
    GalleriaComponent,
    FilterInputComponent,
    ToastComponent,
    CasePipe,
    RequiredFieldsNoteComponent,
  ],
  template: ` <div class="tw-py-8">
    <app-galleria #galleria />
    <app-loading-panel
      loadingStyle="Box"
      [state]="loading ? 'Loading' : 'Loaded'"
      [error]="error"
    >
      <ng-template #loaded>
        <div>
          <app-page-header
            label="Select a base report"
            headerSize="h2"
            labelId="select-report-heading"
          >
            <ng-template #right>
              <app-required-fields-note classes="tw-pb-0 tw-mb-0" />
            </ng-template>
          </app-page-header>

          @if (isReadOnly()) {
            <div class="tw-mb-4">
              <app-toast type="Info">
                The base report cannot be changed when editing an existing
                report.
              </app-toast>
            </div>
          }

          <div>
            @if (!isReadOnly()) {
              <div class="tw-pb-6 tw-flex tw-items-center">
                <app-filter-input
                  [control]="filter"
                  label="Search"
                  recordLabel="report"
                  [recordCount]="filtered.length"
                  classes="tw-w-[400px]"
                />
              </div>
            }

            <div
              class="tw-grid tw-grid-cols-1 tw-gap-3 tw-max-h-[550px] tw-overflow-y-auto tw-pr-2 sm:tw-pr-4 tw-contain-paint"
              role="radiogroup"
              aria-labelledby="select-report-heading"
              aria-required="true"
            >
              @for (report of filtered; track report; let i = $index) {
                @let prevReport = i > 0 ? filtered[i - 1] : null;
                @if (report.typeTag !== prevReport?.typeTag) {
                  <h3
                    class="tw-m-0 tw-font-bold tw-text-xl tw-pl-2 tw-pt-4 tw-text-[var(--sed-black-80-color)]"
                  >
                    {{ report.typeTag | case: 'sentence' }}
                  </h3>
                }
                <app-report
                  [report]="report"
                  [filterValue]="filterValue"
                  [control]="fg.controls.reportTemplate"
                  [disabled]="isReadOnly()"
                  [first]="i === 0"
                  (dblclick)="isReadOnly() ? null : next()"
                  (doubleEnter)="isReadOnly() ? null : next()"
                />
              }
            </div>

            <div class="tw-py-4"></div>
          </div>
        </div>

        <app-action-row>
          <app-button
            right2
            label="Next"
            (click)="next()"
            [disabled]="!fg.valid"
          />
        </app-action-row>
        <div class="tw-text-sm tw-italic tw-pt-8 tw-min-w-0 tw-w-full">
          <span
            >* Reports will include limited claim information for insured claims
            in the state of jurisdiction of Montana, as directed by Montana
            state law.</span
          >
        </div>
      </ng-template>
    </app-loading-panel>
  </div>`,
  styles: [
    `
      .report-scroll-container {
        contain: layout;
      }
    `,
  ],
})
export class SelectReportStepComponent extends BaseComponent implements OnInit {
  private reports = inject(ReportsService);
  private previewMapping = inject(ReportPreviewMappingService);

  @Input()
  stepper: StepperComponent;

  filter = new FormControl<string>(null);

  templates: ReportTemplate[] = [];

  filtered: ReportTemplate[] = [];

  filterValue?: string;
  loading = true;
  error: any = null;

  fg = new FormGroup({
    reportTemplate: new FormControl<ReportTemplate>(null, Validators.required),
  });

  isReadOnly = computed(() => this.reports.isEditPersonalizedMode());

  @HandleLoadingState()
  async ngOnInit() {
    await this.loadReports();
    const reportTemplate = this.reports.currentReport()?.reportTemplate;
    if (reportTemplate) {
      this.fg.controls.reportTemplate.setValue(reportTemplate);
    }

    this.setFiltered();
    this.setupFormListeners();
  }

  private setupFormListeners(): void {
    this.dispose.subs = this.filter.valueChanges.subscribe((value) => {
      this.setFiltered(value);
    });
  }

  async next() {
    if (this.reports.currentReport() == null) {
      const selectedTemplate = this.fg.controls.reportTemplate.value;
      const report = this.reports.createNewReport(selectedTemplate);
      this.reports.currentReport.set(report);
      this.stepper.next();
    } else {
      const selectedTemplate = this.fg.controls.reportTemplate.value;
      let report = this.reports.currentReport();
      if (report.reportTemplate?.id !== selectedTemplate?.id) {
        this.confirm.show({
          label: 'Change base report',
          message: `Are you sure you want to change the selected base report?  All configurations will be discarded.`,
          width: '35rem',
          confirm: async () => {
            this.reports.clearReport();
            report = this.reports.createNewReport(selectedTemplate);
            this.reports.currentReport.set(report);
            this.reports.selectedClient.set('');
            this.stepper.resetStepsAfter(0);
            this.stepper.next();
          },
        });
      } else {
        this.stepper.next();
      }
    }
  }

  @HandleLoadingState()
  private async loadReports() {
    const response = await this.api.reports.getAllReports('JURIS');
    this.templates = this.mapSystemReportsToTemplates(response.systemReports);
    this.sortTemplatesByGroup();
  }

  private mapSystemReportsToTemplates(
    systemReports: SystemReport[],
  ): ReportTemplate[] {
    return systemReports.map((report) => ({
      id: report.id,
      type: ReportCategoryUtil.getCategoryFromGroup(report.group),
      title: report.reportName,
      description: report.description,
      model: report.model,
      favorite: false,
      typeTag: ReportCategoryUtil.getDisplayName(
        ReportCategoryUtil.getCategoryFromGroup(report.group),
      ),
      labelCase: 'none',
      previewSrc: this.previewMapping.getPreviewImage(report.reportName),
      requiresClient: report.requiresClient,
      requiresFilter: report.requiresFilter,
    }));
  }

  private sortTemplatesByGroup() {
    this.templates.sort((a, b) => {
      const orderA = ReportCategoryUtil.getSortOrder(a.typeTag);
      const orderB = ReportCategoryUtil.getSortOrder(b.typeTag);

      if (orderA !== orderB) {
        return orderA - orderB;
      }

      return a.title.localeCompare(b.title);
    });
  }

  private setFiltered(filter?: string) {
    this.filterValue = filter;
    this.filtered = [];
    const lowerFilter = filter?.toLowerCase() || '';
    for (const report of this.templates) {
      if (filter) {
        if (
          report.title.toLowerCase().includes(lowerFilter) ||
          report.description.toLowerCase().includes(lowerFilter)
        ) {
          this.filtered.push(report);
        }
      } else {
        this.filtered.push(report);
      }
    }
  }
}
